import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import {
  SOFTLINE_API_MACHINE_CATALOG_ADDITIONAL_FILTER,
  SOFTLINE_API_MACHINE_CATALOG_CREATE_NOTE,
  SOFTLINE_API_MACHINE_CATALOG_DATA_SHEET,
  SOFTLINE_API_MACHINE_CATALOG_IMAGE,
  SOFTLINE_API_MACHINE_CATALOG_MACHINE_BY_ID,
  SOFTLINE_API_MACHINE_CATALOG_META_CONTROL,
  SOFTLINE_API_MACHINE_CATALOG_META_FORM,
  SOFTLINE_API_MACHINE_CATALOG_NOTES,
} from '../machine-catalog.api';
import { MetaFormBody } from '../types/meta-form-body';
import { Machine } from '../types/machine';
import { lastValueFrom } from 'rxjs';

@Injectable()
export class MachineCatalogRepository {
  readonly #dataAccess = inject(ApiDataAccess);

  async machineById(machineId: string | number): Promise<any> {
    return await this.#dataAccess.get({
      path: SOFTLINE_API_MACHINE_CATALOG_MACHINE_BY_ID,
      pathParams: { machineId },
    });
  }

  async metaForm(body: MetaFormBody): Promise<any> {
    return await this.#dataAccess.create(
      {
        path: SOFTLINE_API_MACHINE_CATALOG_META_FORM,
      },
      body
    );
  }

  async metaControl(body: any): Promise<any> {
    return await this.#dataAccess.create(
      {
        path: SOFTLINE_API_MACHINE_CATALOG_META_CONTROL,
      },
      body
    );
  }

  async additionalFilter(body: any): Promise<Partial<Machine>[]> {
    return await this.#dataAccess.create(
      {
        path: SOFTLINE_API_MACHINE_CATALOG_ADDITIONAL_FILTER,
      },
      body
    );
  }

  async machineImageByPath(
    machineId: number | string,
    imagePath: string,
    height?: number,
    width?: number
  ): Promise<string | null> {
    const result = await lastValueFrom(
      this.#dataAccess.download({
        path: SOFTLINE_API_MACHINE_CATALOG_IMAGE + imagePath,
        pathParams: { machineId },
        queryParams: { height, width },
      })
    );
    if (result?.type !== 'response' || !result.response) return null;
    const blob =
      result.response instanceof Blob ? result.response : new Blob([result.response]);
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async machineNotes(machineId: number | string): Promise<any> {
    return await this.#dataAccess.get({
      path: SOFTLINE_API_MACHINE_CATALOG_NOTES,
      pathParams: { machineId },
    });
  }

  async createMachineNote(machineId: number | string, note: string): Promise<any> {
    return await this.#dataAccess.create(
      {
        path: SOFTLINE_API_MACHINE_CATALOG_CREATE_NOTE,
        pathParams: { machineId },
      },
      { note }
    );
  }

  async machineDataSheet(machineId: number | string): Promise<Blob | null> {
    const result = await lastValueFrom(
      this.#dataAccess.download({
        path: SOFTLINE_API_MACHINE_CATALOG_DATA_SHEET,
        pathParams: { machineId },
      })
    );
    if (result?.type === 'response' && result.response) {
      return result.response instanceof Blob
        ? result.response
        : new Blob([result.response]);
    }
    return null;
  }
}
